import {
  Object,
  Array,
  String,
  Boolean,
  Number,
  Static,
  Optional,
  Record,
  Composite,
  Intersect,
} from '@sinclair/typebox';

import { NonEligibilityReasons } from '../../sas-domain/common';

import {
  DapiError,
  DapiWarning,
  DapiDictionariesFlight,
  DapiDictionariesLocation,
  DapiTravelDocuments,
  DapiSpecialServiceRequests,
  DapiAir,
  DapiService,
  DapiContact,
} from './common';

const Name = Object({
  firstName: String(),
  lastName: String(),
  title: String(),
  nameType: String(),
  isPreferred: Boolean(),
});
type Name = Static<typeof Name>;

const RegulartoryDetails = Object({
  id: String(),
  airlineCode: String(),
  regulatoryApisType: String(),
  regulatoryDocument: Object({ name: Name, gender: String(), birthDate: String() }),
});
type RegulartoryDetails = Static<typeof RegulartoryDetails>;

export const RetrieveReservationTraveler = Object({
  passengerTypeCode: String(),
  id: String(),
  names: Array(Name),
  accompanyingTravelerId: Optional(String()),
  dateOfBirth: Optional(String()),
  regulartoryDetails: Optional(Array(RegulartoryDetails)),
});
export type RetrieveReservationTraveler = Static<typeof RetrieveReservationTraveler>;

const FlightReaccommodationBaseInfo = Object({ airBoundId: String(), isEligible: Boolean() });
type FlightReaccommodationBaseInfo = Static<typeof FlightReaccommodationBaseInfo>;

const ChangeReaccommodation = Composite([
  FlightReaccommodationBaseInfo,
  Intersect([
    Optional(
      Object({ conditions: Optional(Array(Object({ departureDateTime: Object({ start: String(), end: String() }) }))) })
    ),
    Optional(Object({ nonEligibilityReasons: Optional(Array(NonEligibilityReasons)) })),
  ]),
]);
type ChangeReaccommodation = Static<typeof ChangeReaccommodation>;

const AcknowledgeReaccommodation = Composite([
  FlightReaccommodationBaseInfo,
  Optional(Object({ nonEligibilityReasons: Optional(Array(NonEligibilityReasons)) })),
]);
type AcknowledgeReaccommodation = Static<typeof AcknowledgeReaccommodation>;

export const RetrieveReservationDictionary = Object({
  airline: Record(String(), String()),
  location: Record(String(), DapiDictionariesLocation),
  country: Record(String(), String()),
  aircraft: Record(String(), String()),
  flight: Record(String(), DapiDictionariesFlight),
  tax: Record(String(), String()),
  currency: Record(String(), Object({ name: String(), decimalPlaces: Number() })),
  specialServiceRequest: Optional(Record(String(), Object({ name: String() }))),
  bookingStatus: Record(String(), Object({ name: String() })),
  meal: Record(String(), String()),
});
export type RetrieveReservationDictionary = Static<typeof RetrieveReservationDictionary>;

const FlightReaccommodation = Object({
  change: Array(ChangeReaccommodation),
  acknowledge: Array(AcknowledgeReaccommodation),
});
export type FlightReaccommodation = Static<typeof FlightReaccommodation>;

export const RetrieveReservationResponse = Object({
  data: Object({
    id: String(),
    creationPointOfSale: Object({ pointOfSaleId: String(), countryCode: String() }),
    creationDateTime: String(),
    lastModificationDateTime: String(),
    isGroupBooking: Boolean(),
    air: Optional(DapiAir),
    services: Optional(Array(DapiService)),
    travelers: Array(RetrieveReservationTraveler),
    contacts: Array(DapiContact),
    travelDocuments: Array(DapiTravelDocuments),
    remarks: Optional(Array(Object({ id: String(), remarkType: String(), freeText: String() }))),
    specialServiceRequests: Optional(Array(DapiSpecialServiceRequests)),
    orderEligibilities: Optional(Object({ flightReaccommodation: FlightReaccommodation })),
  }),
  dictionaries: RetrieveReservationDictionary,
  errors: Optional(Array(DapiError)),
  warnings: Optional(Array(DapiWarning)),
});
export type RetrieveReservationResponse = Static<typeof RetrieveReservationResponse>;
